import { Component } from "@theme/component";
import { debounce, onAnimationEnd, prefersReducedMotion, requestIdleCallback } from "@theme/utilities";
import { sectionRenderer } from "@theme/section-renderer";
import { morph } from "@theme/morph";
import { RecentlyViewed } from "@theme/recently-viewed-products";
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from "@theme/dialog";

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} [idleContent] - The idle-state block (popular searches, categories,
 * recommended/recently viewed products), a sibling of the results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @property {HTMLElement} [viewAllButton] - The "See results" footer button.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ["searchInput", "predictiveSearchResults", "resetButton"];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Cached fetch of the recently viewed products markup, reused across searches within the
   * same dialog session so it isn't re-requested on every keystroke. Invalidated whenever the
   * dialog opens, the recently viewed products are cleared, or a bfcache restore resumes this
   * page (see `#handlePageShow`).
   * @type {Promise<string | null> | null}
   */
  #recentlyViewedMarkupPromise = null;

  /**
   * The server-rendered recommended products displaced by the recently viewed list, restored
   * when the shopper clears their recently viewed products.
   * @type {Element[]}
   */
  #recommendedProducts = [];

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest("dialog-component");
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener("keydown", this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });

      this.addEventListener("click", this.#handleModalClick, { signal });
    }

    // A bfcache restore resumes this exact JS state without re-running it, so a product viewed
    // right before navigating away (and added to RecentlyViewed on that page) wouldn't otherwise
    // be reflected if the shopper navigates back to a page where the drawer was left open.
    window.addEventListener("pageshow", this.#handlePageShow, { signal });

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest("button") ||
      target.closest("a") ||
      target.closest("input");

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === "k") {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    this.#recentlyViewedMarkupPromise = null;

    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.#injectRecentlyViewed();
  }

  /**
   * Handles the window's pageshow event, firing on both a normal load and a bfcache restore.
   * @param {PageTransitionEvent} event - The pageshow event.
   */
  #handlePageShow = (event) => {
    if (event.persisted) {
      this.#recentlyViewedMarkupPromise = null;
    }
  };

  get #allResultsItems() {
    // The idle block stays in the DOM while hidden in the results state; skip its items.
    const containers = Array.from(
      this.querySelectorAll(
        ".predictive-search-results__wrapper-queries, " +
          ".predictive-search-results__wrapper-products, " +
          ".predictive-search-results__list"
      )
    ).filter((container) => container.checkVisibility());

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains("predictive-search-results__wrapper-products")) {
          return Array.from(container.querySelectorAll('[ref="recentlyViewedItems[]"], [ref="resultsItems[]"]'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"]'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute("aria-selected") === "true") ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove("keyboard-focus");
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute("aria-selected", "true");
        if (this.#isKeyboardNavigation) {
          item.classList.add("keyboard-focus");
        }
        activeItem = item;
      } else {
        item.removeAttribute("aria-selected");
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? "instant" : "smooth", block: "nearest" });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === "Escape") {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === "ArrowLeft" || event.key === "ArrowRight") {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case "ArrowDown":
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case "Tab":
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case "ArrowUp":
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case "Enter": {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector("[data-single-result-url]");
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector("a")?.click();
        } else {
          event.preventDefault();
          window.location.href = this.#searchDestinationUrl;
        }
        break;
      }
    }
  };

  /**
   * Handles a click on the "See results" button.
   * @param {MouseEvent} event - The click event.
   */
  handleViewAllClick = (event) => {
    const suggestedUrl = this.#firstQuerySuggestionUrl;
    if (!suggestedUrl) return;

    event.preventDefault();
    window.location.href = suggestedUrl;
  };

  /**
   * @returns {string} The URL to navigate to when the search is submitted without a
   * specific result selected.
   */
  get #searchDestinationUrl() {
    const suggestedUrl = this.#firstQuerySuggestionUrl;
    if (suggestedUrl) return suggestedUrl;

    const searchUrl = new URL(Theme.routes.search_url, location.origin);
    searchUrl.searchParams.set("q", this.refs.searchInput.value);
    return searchUrl.toString();
  }

  /**
   * @returns {string | null} The `href` of the first Shopify query suggestion currently
   * rendered in the results, if any.
   */
  get #firstQuerySuggestionUrl() {
    return (
      this.refs.predictiveSearchResults.querySelector("[data-first-query-suggestion]")?.getAttribute("href") ?? null
    );
  }

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add("removing");

      onAnimationEnd(recentlyViewedWrapper, () => {
        const anchor = recentlyViewedWrapper.parentElement;
        recentlyViewedWrapper.remove();
        anchor?.replaceChildren(...this.#recommendedProducts);
        this.#recommendedProducts = [];
      });
    }

    this.#recentlyViewedMarkupPromise = null;
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.scrollTo(0, 0);
      this.querySelector(".predictive-search-form__content")?.scrollTo(0, 0);
    });
  }

  /**
   * Reads the `data-results-count` the freshly rendered section left on its content (0 when
   * absent), flips `data-search-state` accordingly, and enables or disables the "See results"
   * footer button.
   * @param {"idle" | "results" | "no-results"} [state] - Overrides the state derived from the count.
   */
  #syncSearchState(state) {
    const { predictiveSearchResults, viewAllButton } = this.refs;

    const countElement = predictiveSearchResults.querySelector("[data-results-count]");
    const count = countElement ? Number(countElement.getAttribute("data-results-count")) : 0;

    this.dataset.searchState = state ?? (count > 0 ? "results" : "no-results");
    viewAllButton?.setAttribute("aria-disabled", count === 0 ? "true" : "false");
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set("q", searchTerm);
    url.searchParams.set("resources[limit_scope]", "each");
    url.searchParams.set("resources[type]", "product,collection,page,article,query");

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then(async (resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        if (abortController.signal.aborted) return;

        this.#syncSearchState();
        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products, reusing the in-flight or already
   * resolved request for the current dialog session instead of re-fetching on every search.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  #getRecentlyViewedProductsMarkup() {
    this.#recentlyViewedMarkupPromise ??= this.#fetchRecentlyViewedProductsMarkup().catch((error) => {
      this.#recentlyViewedMarkupPromise = null;
      throw error;
    });
    return this.#recentlyViewedMarkupPromise;
  }

  /**
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #fetchRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set("q", viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(" OR "));
    url.searchParams.set("resources[type]", "product");

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  /**
   * Injects recently viewed products into the idle block's `[data-predictive-search-products]`
   * anchor, displacing the recommended products rendered there. No-op if there is no idle
   * block or anchor, or if the shopper has no recently viewed products.
   */
  async #injectRecentlyViewed() {
    const collectionElement = this.refs.idleContent?.querySelector("[data-predictive-search-products]");
    if (!collectionElement) return;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return;

    const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
    if (!recentlyViewedMarkup) return;

    const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, "text/html");
    const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.querySelector("[data-predictive-search-products]");
    if (!recentlyViewedProductsHtml) return;

    for (const child of recentlyViewedProductsHtml.children) {
      if (child instanceof HTMLElement) {
        child.setAttribute("ref", "recentlyViewedWrapper");
      }
    }

    this.#recommendedProducts = Array.from(collectionElement.children);
    collectionElement.replaceChildren(...recentlyViewedProductsHtml.children);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = () => {
    const { predictiveSearchResults, searchInput } = this.refs;

    this.#currentIndex = -1;
    searchInput.value = "";
    this.#hideResetButton();

    // Abort any in-flight fetch so a late response can't repopulate the cleared results.
    this.#createAbortController();

    predictiveSearchResults.replaceChildren();
    this.#syncSearchState("idle");
    this.#resetScrollPositions();
  };
}

if (!customElements.get("predictive-search-component")) {
  customElements.define("predictive-search-component", PredictiveSearchComponent);
}
